import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import {
  IntegrationInstallationService,
  type IntegrationInstallationsFilters,
} from '../api/integration-installation-service';
import type { PaginationDto } from '../types/common';
import type {
  IntegrationInstallation,
  ListIntegrationInstallationsResponse,
} from '../types/integration-installation';

export const integrationInstallationQueryKeys = {
  all: ['integration-installations'] as const,
  list: (filters: IntegrationInstallationsFilters, pagination: PaginationDto = {}) =>
    ['integration-installations', 'list', filters, pagination] as const,
  byId: (id: string | null) => ['integration-installations', id] as const,
};

export function useIntegrationInstallations(
  filters: IntegrationInstallationsFilters,
  pagination?: PaginationDto,
): UseQueryResult<ListIntegrationInstallationsResponse> {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: integrationInstallationQueryKeys.list(filters, pagination),
    queryFn: async () => {
      const result = await IntegrationInstallationService.getInstallations(filters, pagination);
      for (const installation of result.items) {
        queryClient.setQueryData(
          integrationInstallationQueryKeys.byId(installation._id),
          installation,
        );
      }
      return result;
    },
  });
}

export function useIntegrationInstallation(
  id: string | null,
  accountId: string | null,
): UseQueryResult<IntegrationInstallation> {
  return useQuery({
    queryKey: integrationInstallationQueryKeys.byId(id),
    queryFn: () =>
      IntegrationInstallationService.getInstallation(id as string, accountId as string),
    enabled: Boolean(id) && Boolean(accountId),
  });
}
